import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getItemPopupList } from "src/apis/itemApi";
import { SearchCodeModalForm } from "src/components/common/form/search/SearchCodeModalForm";
import { SearchDropDownForm } from "src/components/common/form/search/SearchDropDownForm";
import { SearchSectionForm } from "src/components/common/form/search/SearchSectionForm";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { ItemDto } from "src/dtos/item/itemDto";
import useUiuxStore from "src/store/useUiuxStore";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useWMS2010_R01Store, { defaultWMS2010_R01Filter } from "./Store_WMS2010_R01";
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";
import { SubtotalControl } from "./components/SubtotalControl";
import { SearchSpacer } from "src/components/common/common";
import { SearchSectionDoubleForm } from "src/components/common/form/search/SearchSectionDoubleForm";
import LabelPrintButton from "src/components/common/LabelPrintButton";
import { LabelPrintData } from "src/apis/labelApi";
import { generateRowId } from "./utils/tabConfig";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";

interface Props {}

export const SearchSection_WMS2010_R01 = ({}: Props) => {
    const {
        WMS2010_R01Filter, 
        setWMS2010_R01Filter,
        activeTab,
        tabSelectedIds,
        tabData
    } = useWMS2010_R01Store();

    const {
        wmsCdList,    //  물류센터 공통코드
        warehouseCdList,      //  창고 공통코드
        itemGroupList,        //  품목그룹 공통코드
        brandCdList         //  브랜드 공통코드
    } = useCommonCodeStore();

    const { setCodeSearchModalProps } = useUiuxStore();

	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [	
		{
			fieldName: 'itemCd',
			category: '품목코드',
			focusId: 'itemCd_search',
			responseMapping: {
				itemCd: 'itemCd',
				itemNm: 'itemNm',
			},
			extraFilter: () => ({
				itemCd: WMS2010_R01Filter.itemCd || '',
				itemNm: WMS2010_R01Filter.itemNm || '',
			}),
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: WMS2010_R01Filter,
		setEditData: setWMS2010_R01Filter,
		defaultData: defaultWMS2010_R01Filter,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'WMS2010_R01_search'
	});


    // 선택된 LOT 목록 → 라벨 출력용 데이터 (LOT별 재고 탭용)
    const selectedLabelDataList = useMemo((): LabelPrintData[] => {
        if (activeTab !== 'lot') return [];

        const selectedIds = tabSelectedIds.lot;
        const lotData = tabData.lot;

        if (!selectedIds.length || !lotData || lotData.length === 0) return [];

        return selectedIds
            // 선택된 ID 순서를 유지해서 라벨 출력 순서도 사용자가 선택한 흐름을 따름
            .map((selectedId) => lotData.find((row: any) => generateRowId(row, 'lot') === selectedId))
            .filter((item): item is any => Boolean(item?.lotNo))
            .map((item) => {
                // 창고코드가 300이면 자재입고, 그 외엔 생산입고
                const labelType = item.warehouseCd === '300' ? '[자재입고]' : '[생산입고]';
                const qtyNum = item.stockQty ?? (item as any).stockQty ?? 0;
                const unit = item.itemUnit ?? (item as any).itemUnit ?? 'EA';
                const qtyStr = `${Number(qtyNum).toLocaleString()}${unit}`;
                const storageLocation = item.locationCd ?? (item as any).location_cd ?? '';
                const transportDate = item.transportDate ?? (item as any).transport_date ?? '';

                return {
                    lotNo: item.lotNo,
                    itemCd: item.itemCd ?? '',
                    itemNm: item.itemNm ?? '',
                    itemSpec: item.itemSpec ?? '',
                    transportDate,
                    itemAccountNm: item.itemAccountNm ?? '',
                    storageLocation,
                    qty: qtyStr,
                    customer: item.customerNm ?? '',
                    customerItemCd: item.itemCd ?? '',
                    labelType,
                };
            });
    }, [activeTab, tabSelectedIds.lot, tabData.lot]);

    return (
        <div className="w-full">
            <SearchSectionDoubleForm buttonText="조회" >
                {/* 물류센터 선택 */}
                <SearchDropDownForm<SysCodeDto>
                    label="물류센터"
                    value={WMS2010_R01Filter.logicCenter}
                    optionList={wmsCdList}
                    onChangeValue={(v) =>
                        setWMS2010_R01Filter({ logicCenter: v === "all" ? "" : v, })
                    }
                />

                {/* 창고 선택 */}
                <SearchDropDownForm<WareHouseDto>
                    label="창고명"
                    value={WMS2010_R01Filter.warehouseCd}
                    optionList={warehouseCdList}
                    onChangeValue={(v) =>
                        setWMS2010_R01Filter({ warehouseCd: v === "all" ? "" : v, })
                    }
                    />

                {/* 브랜드 선택 */}
                <SearchDropDownForm<SysCodeDto>
                    label="브랜드"
                    value={WMS2010_R01Filter.brandCd}
                    optionList={brandCdList}
                    onChangeValue={(v) =>
                        setWMS2010_R01Filter({ brandCd: v === "all" ? "" : v, })
                    }
                />



                {/* 품목그룹 선택 */}
                <SearchDropDownForm<SysCodeDto>
                    label="품목그룹"
                    value={WMS2010_R01Filter.itemGroup}
                    optionList={itemGroupList}
                    onChangeValue={(v) =>
                        setWMS2010_R01Filter({ itemGroup: v === "all" ? "" : v, })
                    }
                />

                                {/* 품목 검색 */}
                <SearchCodeModalForm
                    label="품목코드"
                    value={WMS2010_R01Filter.itemCd}
                    valueLabel={WMS2010_R01Filter.itemNm}
                    handleFoucsId="itemCd_search"
                    onChange={getFieldHandlers('itemCd').onChange}
                    onDoubleClick={getFieldHandlers('itemCd').onDoubleClick}
                    onKeyDown={getFieldHandlers('itemCd').onEnterKeyDown}
                />

                {/* 소계 컨트롤 */}
                <SubtotalControl />
                <SearchSpacer/>
                {/* 라벨 출력 버튼 (LOT 탭에서만 활성화) */}
                <LabelPrintButton
                    labelDataList={selectedLabelDataList}
                    disabled={activeTab !== 'lot' || selectedLabelDataList.length === 0}
                    buttonText={activeTab === 'lot' ? '라벨 출력' : '라벨 출력 (LOT탭 전용)'}
                />
            </SearchSectionDoubleForm>
        </div>
    );
};
